import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import Input from "components/atoms/Input";
import Text from "components/atoms/Text";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Drug } from "common/models/types";
import { fetchDrugs } from "api/drugService";
import ReactDOM from "react-dom";
import Spinner from "components/atoms/Spinner";
import Icon from "components/atoms/Icon";
import { log, LogLevel } from "utils/LogUtil";
import { convertibleToKor, korPreview } from "utils/engToKor";

interface DrugSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (drug: Drug) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  type?: "search" | "productName" | "otc" | "hff";
  id?: string;
  autoSearchOnNineDigits?: boolean;
  itemCode?: string; // 품목기준코드를 prop으로 받아 이미 선택된 상태인지 확인
  onManualInput?: () => void; // 검색 결과 없을 때 직접 입력 전환 콜백
  onEmptyEnter?: () => void; // 입력값이 비어 있을 때 엔터 → 다음 필드 이동 등 호출부가 정의
}

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SpinnerContainer = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ResultsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 400px;
  min-width: 400px;
  max-height: 300px;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow-y: auto;
  margin-top: 4px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
  &::-webkit-scrollbar-thumb {
    background: ${COLOR_SYSTEM.get("Gray")?.[300]};
    border-radius: 3px;
  }
`;

const ResultItem = styled.div<{ isSelected: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  background-color: ${(props) =>
    props.isSelected ? COLOR_SYSTEM.get("Gray")?.[100] : "transparent"};
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const NoResultItem = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
`;

const ManualInputArea = styled.div`
  position: sticky;
  bottom: 0;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const ManualInputLabel = styled.span`
  display: block;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  font-size: 13px;
  font-weight: 500;
`;

const ManualInputHint = styled.span`
  display: block;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-size: 11px;
  margin-top: 2px;
`;

const KorHintContainer = styled.div`
  position: absolute;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const DrugSearchInput: React.FC<DrugSearchInputProps> = ({
  value,
  onChange,
  onSelect,
  placeholder,
  label,
  disabled,
  type = "search",
  id,
  autoSearchOnNineDigits = false,
  itemCode,
  onManualInput,
  onEmptyEnter,
}) => {
  log(LogLevel.VERBOSE, "[DrugSearchInput] 렌더링:", { value, label });

  const [searchQuery, setSearchQuery] = useState<string>(value);
  const [results, setResults] = useState<Drug[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false); // 검색을 수행했는지 추적
  const [korHint, setKorHint] = useState<string | null>(null); // 입력 중 영타→한타 실시간 변환 미리보기
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false); // 렌더 조건용 (isFocused ref는 리렌더를 못 일으킴)
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const isFocused = useRef<boolean>(false);
  const isSelectingRef = useRef(false);
  const hasJustSelectedRef = useRef(false); // 약품을 방금 선택했는지 추적
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // 외부 클릭 시 결과 창 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 약품 선택 처리를 위한 공통 함수
  const handleDrugSelect = useCallback(
    (drug: Drug) => {
      log(LogLevel.INFO, "[DrugSearchInput] 약품 선택 시작:", {
        productName: drug.productName,
        drug,
        isSelectingRef: isSelectingRef.current,
        isFocused: isFocused.current,
        showResults,
        resultsCount: results.length,
      });

      const productName = drug.productName || "";
      isSelectingRef.current = true;
      hasJustSelectedRef.current = true; // 약품을 방금 선택했음을 표시

      // 검색 결과 완전 초기화
      setResults([]);
      setShowResults(false);
      setSelectedIndex(-1);
      setSearchQuery(""); // searchQuery를 빈 문자열로 초기화
      setKorHint(null);

      onSelect(drug);

      log(LogLevel.INFO, "[DrugSearchInput] 약품 선택 완료:", {
        productName,
        isSelectingRef: isSelectingRef.current,
        hasJustSelectedRef: hasJustSelectedRef.current,
        showResults: false,
        selectedIndex: -1,
        searchQueryCleared: true,
        resultsCleared: true,
      });

      setTimeout(() => {
        isSelectingRef.current = false;
        log(LogLevel.VERBOSE, "[DrugSearchInput] isSelectingRef 초기화 완료");
      }, 50);
      
      // 약품 선택 후 일정 시간 후에 플래그 초기화
      setTimeout(() => {
        hasJustSelectedRef.current = false;
        log(LogLevel.VERBOSE, "[DrugSearchInput] hasJustSelectedRef 초기화 완료");
      }, 500);
    },
    [onSelect, showResults, results.length]
  );

  // 초기 검색 함수
  const searchDrugs = useCallback(
    async (searchTerm: string, forceInsuranceCode: boolean = false) => {
      try {
        log(LogLevel.VERBOSE, "[DrugSearchInput] 초기 검색 시작:", {
          searchTerm,
          isFocused: isFocused.current,
          forceInsuranceCode,
        });

        if (!searchTerm || searchTerm.length < 2) {
          setResults([]);
          setLoading(false);
          setHasMore(true);
          log(LogLevel.VERBOSE, "[DrugSearchInput] 검색어 부족 (2글자 미만)");
          return;
        }

        setLoading(true);

        // 타입별 검색 옵션 구성 (1차 검색과 한타 변환 재검색이 공유)
        const buildSearchOptions = (term: string) => {
          const searchOptions: any = {
            offset: 0,
            limit: 10,
          };
          if (type === "search") {
            searchOptions.search = term;
          } else if (type === "productName") {
            searchOptions.productName = term;
          } else if (type === "otc") {
            searchOptions.specialtyOtc = "일반";
            searchOptions.search = term;
          } else if (type === "hff") {
            searchOptions.specialtyOtc = "HFF";
            searchOptions.search = term;
          }
          return searchOptions;
        };

        let options: any;

        // 9자리 숫자 자동 검색 시 보험코드로 검색
        if (forceInsuranceCode) {
          options = { offset: 0, limit: 10, insuranceCode: searchTerm };
          log(LogLevel.INFO, "[DrugSearchInput] 보험코드로 검색:", { insuranceCode: searchTerm });
        } else {
          options = buildSearchOptions(searchTerm);
        }

        // 영타 오입력 대비: 변환 가능하면 변환어 검색을 미리 병렬로 던져 둔다
        // (원문 결과가 있으면 버리고, 0건일 때만 사용 — 순차 재검색 왕복 제거)
        const korTerm = forceInsuranceCode ? null : convertibleToKor(searchTerm);
        const korPromise =
          korTerm && korTerm.length >= 2
            ? fetchDrugs(buildSearchOptions(korTerm)).catch(() => null)
            : null;

        const response = await fetchDrugs(options);

        log(LogLevel.VERBOSE, "[DrugSearchInput] 초기 검색 응답:", {
          hasData: !!response.data,
          resultsCount: response.data?.length || 0,
        });

        // 보험코드 검색 결과가 없으면 통합 검색으로 재시도
        let finalResponse = response;
        if (forceInsuranceCode && (!response.data || response.data.length === 0)) {
          log(LogLevel.INFO, "[DrugSearchInput] 보험코드 검색 결과 없음, 통합 검색으로 재시도");
          
          // 통합 검색 옵션으로 변경
          const fallbackOptions = {
            offset: 0,
            limit: 10,
            search: searchTerm,
          };
          
          finalResponse = await fetchDrugs(fallbackOptions);

          log(LogLevel.VERBOSE, "[DrugSearchInput] 통합 검색 재시도 응답:", {
            hasData: !!finalResponse.data,
            resultsCount: finalResponse.data?.length || 0,
          });
        }

        // 여전히 0건이면 병렬로 받아 둔 한타 변환 검색 결과를 사용 (예: "xkdlfpshf" → "타이레놀")
        if ((!finalResponse.data || finalResponse.data.length === 0) && korTerm && korPromise) {
          const korResponse = await korPromise;
          if (korResponse && korResponse.data && korResponse.data.length > 0) {
            log(LogLevel.INFO, "[DrugSearchInput] 검색 결과 0건, 영타→한타 변환 결과 사용:", {
              searchTerm,
              korTerm,
            });
            finalResponse = korResponse;
            // 입력창 표기와 더보기 페이징 기준을 변환어로 갱신
            onChange(korTerm);
            setSearchQuery(korTerm);
            setKorHint(null);
          }
        }

        setHasSearched(true); // 검색 수행됨 표시
        
        if (finalResponse.data && finalResponse.data.length > 0) {
          // 자동 검색 옵션이 활성화되고 결과가 1개인 경우
          if (autoSearchOnNineDigits && finalResponse.data.length === 1) {
            log(LogLevel.INFO, "[DrugSearchInput] 검색 결과 1개, 자동 선택:", {
              drug: finalResponse.data[0].productName,
            });
            // 드롭다운 표시하지 않고 바로 선택
            setShowResults(false);
            setTimeout(() => {
              handleDrugSelect(finalResponse.data[0]);
            }, 100);
          } else {
            // 기존 로직: 드롭다운 표시
            setResults(finalResponse.data);
            setShowResults(true);
            setHasMore(finalResponse.data.length === 10);
            // 첫 번째 항목을 자동으로 선택 상태로 설정
            setSelectedIndex(0);
            log(LogLevel.VERBOSE, "[DrugSearchInput] 초기 검색 결과, 첫 번째 항목 선택:", {
              count: finalResponse.data.length,
              firstItem: finalResponse.data[0]?.productName,
            });
          }
        } else {
          // 검색 결과가 없는 경우
          setResults([]);
          setHasMore(false);
          setSelectedIndex(-1);
          setShowResults(true); // 결과 창은 표시하되 안내 메시지를 보여줌
          log(LogLevel.INFO, "[DrugSearchInput] 검색 결과 없음:", { searchTerm });
        }
      } catch (error) {
        log(LogLevel.ERROR, "[DrugSearchInput] 초기 검색 중 오류 발생:", error);
        setResults([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [type, autoSearchOnNineDigits, handleDrugSelect, onChange]
  );

  // 추가 데이터 로드 함수
  const loadMoreDrugs = useCallback(async () => {
    try {
      if (!searchQuery || searchQuery.length < 2) return;

      log(LogLevel.VERBOSE, "[DrugSearchInput] 추가 데이터 로드 시작:", {
        searchQuery,
        currentResultsCount: results.length,
      });

      setIsLoadingMore(true);

      let options: any = {
        offset: results.length,
        limit: 10,
      };
      if (type === "search") {
        options.search = searchQuery;
      } else if (type === "productName") {
        options.productName = searchQuery;
      } else if (type === "otc") {
        options.specialtyOtc = "일반";
        options.search = searchQuery;
      } else if (type === "hff") {
        options.specialtyOtc = "HFF";
        options.search = searchQuery;
      }

      const response = await fetchDrugs(options);

      log(LogLevel.VERBOSE, "[DrugSearchInput] 추가 데이터 로드 응답:", {
        hasData: !!response.data,
        resultsCount: response.data?.length || 0,
        currentResultsCount: results.length,
      });

      if (response.data && response.data.length > 0) {
        setResults((prev) => {
          const newResults = [...prev, ...response.data];
          log(LogLevel.VERBOSE, "[DrugSearchInput] 추가 데이터 로드 결과:", {
            prevCount: prev.length,
            newCount: newResults.length,
            addedCount: response.data.length,
          });
          return newResults;
        });
        setHasMore(response.data.length === 10);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      log(LogLevel.ERROR, "[DrugSearchInput] 추가 데이터 로드 중 오류 발생:", error);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [searchQuery, results.length, type]);

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      if (!target) return;

      const { scrollTop, scrollHeight, clientHeight } = target;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      log(LogLevel.VERBOSE, "[DrugSearchInput] 스크롤 위치:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        isNearBottom,
        isLoadingMore,
        hasMore,
        loading,
      });

      if (isNearBottom && !isLoadingMore && hasMore && !loading) {
        log(LogLevel.VERBOSE, "[DrugSearchInput] 스크롤 하단 도달, 추가 데이터 로드");
        loadMoreDrugs();
      }
    },
    [isLoadingMore, hasMore, loading, loadMoreDrugs]
  );

  // 상태 변화 추적을 위한 로그
  useEffect(() => {
    log(LogLevel.VERBOSE, "[DrugSearchInput] 상태 업데이트:", {
      searchQuery,
      resultsCount: results.length,
      loading,
      showResults,
      selectedIndex,
      isFocused: isFocused.current,
      hasFocus: document.activeElement === inputRef.current,
    });
  }, [searchQuery, results, loading, showResults, selectedIndex, isFocused]);

  // 드롭다운 위치 계산 (검색 결과·한글 변환 힌트가 공유)
  useEffect(() => {
    if ((showResults || korHint) && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 100000,
      });
    }
  }, [showResults, results.length, korHint]);

  // 검색어 변경 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const newValue = e.target.value;
    log(LogLevel.VERBOSE, "[DrugSearchInput] 입력 변경:", {
      newValue,
      oldValue: searchQuery,
    });

    setSearchQuery(newValue);
    setShowResults(false);
    setHasMore(true);
    setSelectedIndex(-1); // 검색어 변경 시 선택 인덱스 초기화
    setHasSearched(false); // 새로운 검색어 입력 시 초기화
    setKorHint(korPreview(newValue)); // 영타 오입력이면 실시간 한글 변환 미리보기

    if (!newValue) {
      setResults([]);
    }

    onChange(newValue);

    // 9자리 숫자 자동 검색 (옵션이 활성화되고 productName 타입이 아닐 때)
    if (autoSearchOnNineDigits && type !== "productName" && /^\d{9}$/.test(newValue)) {
      log(LogLevel.INFO, "[DrugSearchInput] 9자리 숫자 감지, 보험코드로 자동 검색 실행:", { newValue });
      searchDrugs(newValue, true); // forceInsuranceCode = true
    }
  };

  // 포커스 처리
  const handleFocus = () => {
    log(LogLevel.INFO, "[DrugSearchInput] 포커스 얻음:", {
      searchQuery,
      resultsCount: results.length,
      showResults,
      isSelecting: isSelectingRef.current,
      isFocused: isFocused.current,
    });
    isFocused.current = true;
    setIsInputFocused(true);
  };

  // 포커스 해제 처리
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    log(LogLevel.INFO, "[DrugSearchInput] 포커스 잃음:", {
      searchQuery,
      resultsCount: results.length,
      showResults,
      isSelecting: isSelectingRef.current,
      hasJustSelected: hasJustSelectedRef.current,
      isFocused: isFocused.current,
      value,
    });

    setIsInputFocused(false);

    // 약품을 방금 선택한 경우 재검색하지 않음
    if (hasJustSelectedRef.current) {
      log(LogLevel.INFO, "[DrugSearchInput] 약품 선택 직후이므로 재검색 스킵");
      isFocused.current = false;
      setShowResults(false);
      return;
    }

    if (isSelectingRef.current) {
      log(LogLevel.VERBOSE, "[DrugSearchInput] 선택 중이므로 포커스 상태 유지");
      setTimeout(() => {
        isFocused.current = false;
        log(LogLevel.VERBOSE, "[DrugSearchInput] 선택 완료 후 포커스 해제");
      }, 100);
    } else {
      isFocused.current = false;
      
      // 이미 품목기준코드가 있으면 (약품이 선택된 상태) 재검색하지 않음
      if (itemCode) {
        log(LogLevel.INFO, "[DrugSearchInput] 이미 약품이 선택된 상태, 재검색 스킵:", { 
          value, 
          itemCode 
        });
        setShowResults(false);
        return;
      }
      
      // 포커스아웃 시 자동 검색 실행
      // 조건: 입력값이 2글자 이상이고, 검색 결과가 없을 때
      if (value && value.length >= 2 && !showResults) {
        log(LogLevel.INFO, "[DrugSearchInput] 포커스아웃 시 자동 검색 실행:", { value });
        
        // 9자리 숫자인 경우 보험코드로 검색
        if (/^\d{9}$/.test(value)) {
          log(LogLevel.INFO, "[DrugSearchInput] 9자리 숫자 감지, 보험코드로 검색");
          searchDrugs(value, true); // forceInsuranceCode = true
        }
        // 13자리 숫자인 경우 표준코드(바코드)로 검색
        else if (/^\d{13}$/.test(value)) {
          log(LogLevel.INFO, "[DrugSearchInput] 13자리 숫자 감지, 표준코드로 검색");
          searchDrugs(value, false); // 일반 검색으로 처리 (표준코드도 search로 검색됨)
        }
        // 그 외의 경우 일반 텍스트 검색
        else {
          log(LogLevel.INFO, "[DrugSearchInput] 일반 텍스트로 검색");
          searchDrugs(value, false);
        }
      } else {
        // 검색 결과 숨김
        setShowResults(false);
        log(LogLevel.VERBOSE, "[DrugSearchInput] 즉시 포커스 해제 및 검색 결과 숨김");
      }
    }
  };

  // Input 참조 설정
  const setInputReference = (el: HTMLInputElement | null) => {
    inputRef.current = el;
  };

  // 키보드 이벤트 처리 (Enter, Tab, 위/아래 화살표, Escape)
  // 이 입력의 키 입력은 이 핸들러에서만 처리한다 (IME 조합 가드는 Input 원자에서 공통 처리)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      log(LogLevel.VERBOSE, "[DrugSearchInput] 키 입력:", {
        key: e.key,
        showResults,
        resultsCount: results.length,
        selectedIndex,
        value,
      });

      // Tab 키 처리 - itemCode가 없을 때만 엔터처럼 동작
      if (e.key === "Tab") {
        // itemCode가 이미 있으면 기본 탭 동작 (다음 필드로 이동)
        if (itemCode) {
          log(LogLevel.VERBOSE, "[DrugSearchInput] itemCode 존재, Tab키 기본 동작 허용");
          return;
        }
        
        // 검색 결과가 표시된 상태
        if (showResults && results.length > 0) {
          e.preventDefault();
          // 첫 번째 항목 자동 선택
          const indexToSelect = selectedIndex >= 0 ? selectedIndex : 0;
          handleDrugSelect(results[indexToSelect]);
          log(LogLevel.INFO, "[DrugSearchInput] Tab키로 약품 선택:", {
            drug: results[indexToSelect].productName,
          });
          return;
        }
        
        // 검색 결과가 없고 입력값이 있는 경우
        if (!showResults && value && value.length >= 2) {
          e.preventDefault();
          log(LogLevel.INFO, "[DrugSearchInput] Tab키로 검색 실행:", { value });
          
          // 9자리 숫자인 경우 보험코드로 검색
          if (/^\d{9}$/.test(value)) {
            searchDrugs(value, true);
          }
          // 13자리 숫자인 경우 표준코드로 검색
          else if (/^\d{13}$/.test(value)) {
            searchDrugs(value, false);
          }
          // 그 외의 경우 일반 텍스트 검색
          else {
            searchDrugs(value, false);
          }
          return;
        }
        
        // 입력값 2글자 미만: 기본 탭 동작 (다음 필드로 이동)
        log(LogLevel.VERBOSE, "[DrugSearchInput] Tab키 기본 동작 허용");
      }

      // Enter 키 처리
      if (e.key === "Enter") {
        e.preventDefault();

        // 검색 결과가 있고 선택된 항목이 있으면 해당 항목 선택
        if (showResults && results.length > 0 && selectedIndex >= 0) {
          handleDrugSelect(results[selectedIndex]);
          return;
        }

        // 빈 칸이면 필드 이동 콜백 (호출부가 다음 필드 focus 등을 정의)
        if (!value || !value.trim()) {
          if (onEmptyEnter) {
            log(LogLevel.INFO, "[DrugSearchInput] 빈 칸 엔터, onEmptyEnter 호출");
            onEmptyEnter();
          }
          return;
        }

        // 현재 입력값으로 검색 실행
        setSearchQuery(value);
        if (value.length >= 2) {
          log(LogLevel.INFO, "[DrugSearchInput] 엔터로 검색 실행:", { value });
          searchDrugs(value);
        } else {
          // 검색어가 2글자 미만이면 검색 결과 초기화
          log(LogLevel.VERBOSE, "[DrugSearchInput] 엔터 - 검색어 부족 (2글자 미만)");
          setShowResults(false);
          setResults([]);
        }
        return;
      }

      if (!showResults || results.length === 0) return;

      // 아래 화살표
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const newIndex = selectedIndex < results.length - 1 ? selectedIndex + 1 : selectedIndex;
        setSelectedIndex(newIndex);
        setShowResults(true);
        
        // 선택된 항목으로 스크롤
        setTimeout(() => {
          const selectedItem = resultsContainerRef.current?.querySelector(`[data-index="${newIndex}"]`);
          if (selectedItem && resultsContainerRef.current) {
            const itemTop = (selectedItem as HTMLElement).offsetTop;
            const itemBottom = itemTop + (selectedItem as HTMLElement).offsetHeight;
            const containerTop = resultsContainerRef.current.scrollTop;
            const containerBottom = containerTop + resultsContainerRef.current.clientHeight;
            
            if (itemBottom > containerBottom) {
              resultsContainerRef.current.scrollTop = itemBottom - resultsContainerRef.current.clientHeight;
            } else if (itemTop < containerTop) {
              resultsContainerRef.current.scrollTop = itemTop;
            }
          }
        }, 0);
      }
      // 위 화살표
      else if (e.key === "ArrowUp") {
        e.preventDefault();
        const newIndex = selectedIndex > 0 ? selectedIndex - 1 : 0;
        setSelectedIndex(newIndex);
        setShowResults(true);
        
        // 선택된 항목으로 스크롤
        setTimeout(() => {
          const selectedItem = resultsContainerRef.current?.querySelector(`[data-index="${newIndex}"]`);
          if (selectedItem && resultsContainerRef.current) {
            const itemTop = (selectedItem as HTMLElement).offsetTop;
            const itemBottom = itemTop + (selectedItem as HTMLElement).offsetHeight;
            const containerTop = resultsContainerRef.current.scrollTop;
            const containerBottom = containerTop + resultsContainerRef.current.clientHeight;
            
            if (itemTop < containerTop) {
              resultsContainerRef.current.scrollTop = itemTop;
            } else if (itemBottom > containerBottom) {
              resultsContainerRef.current.scrollTop = itemBottom - resultsContainerRef.current.clientHeight;
            }
          }
        }, 0);
      }
      // Escape
      else if (e.key === "Escape") {
        setShowResults(false);
      }
    },
    [results, selectedIndex, showResults, handleDrugSelect, searchDrugs, value, itemCode, onEmptyEnter]
  );

  useEffect(() => {
    log(LogLevel.VERBOSE, "[DrugSearchInput] 컴포넌트 마운트");
    return () => {
      log(LogLevel.VERBOSE, "[DrugSearchInput] 컴포넌트 언마운트");
    };
  }, []);

  // 변환 힌트 클릭 → 입력값을 한글로 치환하고 바로 검색
  const applyKorHint = useCallback(() => {
    if (!korHint) return;
    const term = korHint;
    log(LogLevel.INFO, "[DrugSearchInput] 한글 변환 힌트 적용:", { term });
    onChange(term);
    setSearchQuery(term);
    setKorHint(null);
    searchDrugs(term);
  }, [korHint, onChange, searchDrugs]);

  return (
    <SearchContainer ref={containerRef}>
      <Input
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        ref={setInputReference}
        id={id}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "약품명 또는 보험코드 입력"}
        label={label}
        disabled={disabled}
        onClick={() => {
          // 클릭 시 검색 결과 표시 로직 제거 - 엔터로만 검색
        }}
        autoComplete="off"
        leftItems={[
          <Icon
            key="search-icon"
            name="Search"
            size={18}
            color={COLOR_SYSTEM.get("Gray")?.[400]}
          />,
        ]}
        rightItems={
          loading
            ? [
                <SpinnerContainer key="spinner-container">
                  <Spinner size="Small" color={COLOR_SYSTEM.get("Gray")?.[400]} />
                </SpinnerContainer>,
              ]
            : undefined
        }
      />
      {showResults &&
        (results.length > 0 || hasSearched) &&
        ReactDOM.createPortal(
          <ResultsContainer style={dropdownStyle} ref={resultsContainerRef} onScroll={handleScroll}>
            {results.length === 0 && hasSearched ? (
              <NoResultItem>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                  검색 결과가 없습니다
                  <br />
                  <br />
                  <div style={{ textAlign: "left", fontSize: "12px", lineHeight: "1.5" }}>
                    <strong>검색 팁:</strong>
                    <br />
                    • 특수문자 전까지만 입력해보세요.
                    <br />
                    • 영어 단위(mg, ml 등)는 제거하고 검색해주세요
                    <br />
                    • 제형을 신경써주세요 ('타이레놀500' → '타이레놀정500')
                  </div>
                </Text>
              </NoResultItem>
            ) : (
              results.map((drug, index) => (
                <ResultItem
                key={`drug-${index}`}
                data-index={index}
                isSelected={index === selectedIndex}
                onMouseDown={(e) => {
                  log(LogLevel.VERBOSE, "[DrugSearchInput] ResultItem onMouseDown 시작:", {
                    drug: drug.productName,
                    isSelecting: isSelectingRef.current,
                    isFocused: isFocused.current,
                  });
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  log(LogLevel.INFO, "[DrugSearchInput] ResultItem onClick 시작:", {
                    drug: drug.productName,
                    isSelecting: isSelectingRef.current,
                    isFocused: isFocused.current,
                    showResults,
                    selectedIndex,
                  });
                  e.preventDefault();
                  e.stopPropagation();
                  handleDrugSelect(drug);
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[900]}>
                    {drug.productName}
                  </Text>
                  {drug.companyName && (
                    <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                      {drug.companyName}
                    </Text>
                  )}
                </div>
              </ResultItem>
            ))
            )}
            {onManualInput && hasSearched && (
              <ManualInputArea
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowResults(false);
                  onManualInput();
                }}
              >
                <ManualInputLabel>직접 입력</ManualInputLabel>
                <ManualInputHint>허가사항 연동 없이 제품명만 등록됩니다</ManualInputHint>
              </ManualInputArea>
            )}
            {isLoadingMore && (
              <ResultItem isSelected={false}>
                <SpinnerContainer>
                  <Spinner size="Small" color={COLOR_SYSTEM.get("Gray")?.[400]} />
                </SpinnerContainer>
              </ResultItem>
            )}
          </ResultsContainer>,
          document.body
        )}
      {korHint &&
        isInputFocused &&
        !showResults &&
        !loading &&
        ReactDOM.createPortal(
          <KorHintContainer
            style={dropdownStyle}
            onMouseDown={(e) => {
              // 입력 포커스를 유지한 채 클릭 처리
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              applyKorHint();
            }}
          >
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[900]}>
              한글로 변환: <strong>{korHint}</strong>
            </Text>
            <ManualInputHint>클릭하거나 Enter를 누르면 한글로 검색됩니다</ManualInputHint>
          </KorHintContainer>,
          document.body
        )}
    </SearchContainer>
  );
};

export default DrugSearchInput;
